<template>
  <sf-dialog v-model="dialog" attach>
    <template v-if="guestQuote" #activator="{ props: activatorProps }">
      <sf-text-button
        ref="declineButton"
        v-bind="activatorProps"
        color=""
        class="text-medium-emphasis mr-1"
        :loading="rejecting"
        :disabled="disabled"
      >
        {{ $t('decline') }}
      </sf-text-button>
    </template>
    <template v-else #activator="{ props: activatorProps }">
      <sf-large-button
        ref="declineButton"
        text
        v-bind="activatorProps"
        color=""
        variant="text"
        class="text-medium-emphasis"
        :block="$vuetify.display.xs"
        :loading="rejecting"
        :disabled="disabled"
      >
        {{ $t('decline') }}
      </sf-large-button>
    </template>
    <v-card>
      <v-card-item>
        <v-card-title>
          <sf-heading ref="title">{{ titleLabel }}</sf-heading>
        </v-card-title>
      </v-card-item>
      <v-card-text v-if="type === 'QUOTE'">{{ $t('quotes.declineQuoteDialog') }}</v-card-text>
      <v-card-text>
        <span>
          <sf-debounced-textarea
            ref="rejectionCommentArea"
            v-model="rejectionCommentValue"
            :label="$t('declineReason')"
            clearable
            auto-grow
            density="compact"
            hide-details="auto"
            variant="outlined"
            color="primary"
            :maxlength="DEFAULT_COMMENT_LENGTH"
            autocomplete="off"
            :hint="
              rejectionCommentValue && rejectionCommentValue.length >= DEFAULT_COMMENT_LENGTH
                ? $t('maxLength', [DEFAULT_COMMENT_LENGTH])
                : ''
            "
            :rules="[noInvalidSpacesRule]"
            persistent-hint
          />
        </span>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <sf-text-button color="grey" class="text-medium-emphasis" @click="dialog = false">
          {{ $t('cancel') }}
        </sf-text-button>
        <sf-text-button color="error" @click="onClickReject">
          {{ $t('decline') }}
        </sf-text-button>
      </v-card-actions>
    </v-card>
  </sf-dialog>
</template>

<script setup lang="ts">
import SfLargeButton from '@/components/button/SfLargeButton.vue'
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import SfDialog from '@/components/dialog/SfDialog.vue'
import SfDebouncedTextarea from '@/components/input/SfDebouncedTextarea.vue'
import { CommentType, DEFAULT_COMMENT_LENGTH } from '@/types'
import { computed, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { noInvalidSpacesRule } from '@/rules'

const props = defineProps({
  rejecting: {
    type: Boolean,
    required: true
  },
  rejectionComment: {
    type: String,
    default: ''
  },
  type: {
    type: String as PropType<CommentType>,
    required: true
  },
  guestQuote: {
    type: Boolean,
    required: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})
const emits = defineEmits(['reject'])
const { t } = useI18n()
const dialog = ref(false)
const rejectionCommentValue = ref<string | undefined>(props.rejectionComment)

const titleLabel = computed(() => {
  switch (props.type) {
    case 'ORDER':
      return t('order.declineOrder')
    case 'QUOTE':
      return t('quotes.declineQuote')
    default:
      return 'undefined'
  }
})

const onClickReject = () => {
  emits('reject', rejectionCommentValue.value)
  dialog.value = false
}
</script>
